<script lang="ts">
    import { selectAndGotoTab, type TabClass } from "@store/pageTracker"
    import { createEventDispatcher, onMount } from "svelte"
    import EditableTabField from "./EditableTabField.svelte"

    export let tab: TabClass
    export let selected = false

    const dispatch = createEventDispatcher<{
        select: void
        scrollstatechange: void
    }>()

    let tabButton: HTMLDivElement

    $: title = tab.displayName
    $: untitled = !tab.nameWithoutEmoji
    $: location = tab.path

    $: if (selected) scrollSelectedTabIntoView("smooth")

    onMount(() => {
        if (selected) scrollSelectedTabIntoView("auto")
    })

    function scrollSelectedTabIntoView(behavior: ScrollBehavior) {
        if (!tabButton) return

        tabButton.scrollIntoView({
            behavior,
            block: "nearest",
            inline: "center",
        })

        window.requestAnimationFrame(() => dispatch("scrollstatechange"))
    }

    function selectTab() {
        if (!selected) selectAndGotoTab(tab.id)
        dispatch("select")
    }

    function handleKeydown(event: KeyboardEvent) {
        if (event.target !== event.currentTarget) return
        if (event.key !== "Enter" && event.key !== " ") return

        event.preventDefault()
        selectTab()
    }

    function handleWheel(event: WheelEvent) {
        if (event.target instanceof HTMLElement && event.target.closest(".editor-form")) return
        if (!(event.currentTarget instanceof HTMLElement)) return

        const scroller = event.currentTarget.closest(".mobile-tab-scroller")
        if (!(scroller instanceof HTMLElement)) return

        const dominantDelta =
            Math.abs(event.deltaX) >= Math.abs(event.deltaY) ? event.deltaX : event.deltaY
        if (dominantDelta === 0) return

        const unitMultiplier =
            event.deltaMode === WheelEvent.DOM_DELTA_PAGE
                ? scroller.clientWidth
                : event.deltaMode === WheelEvent.DOM_DELTA_LINE
                  ? 16
                  : 1

        event.preventDefault()
        scroller.scrollLeft += dominantDelta * unitMultiplier
        window.requestAnimationFrame(() => dispatch("scrollstatechange"))
    }
</script>

<div
    bind:this={tabButton}
    class="mobile-reading-tab"
    class:hasEmoji={Boolean(tab.emoji)}
    class:selected
    role="button"
    tabindex="0"
    aria-current={selected ? "page" : undefined}
    aria-label={`${selected ? "Current reading tab" : "Open reading tab"}: ${title}, ${location}`}
    title={`${title} · ${location}`}
    on:click={selectTab}
    on:keydown={handleKeydown}
    on:wheel={handleWheel}
>
    {#if tab.emoji}
        <span class="tab-icon" aria-hidden="true">
            <span class="tab-emoji">{tab.emoji}</span>
        </span>
    {/if}
    <span class="tab-copy">
        <span class="tab-title" class:untitled={!selected && untitled}>
            {#if selected}
                <EditableTabField {tab} kind="name" displayValue={title} />
            {:else}
                {title}
            {/if}
        </span>
        <span class="tab-location">
            {#if selected}
                <EditableTabField {tab} kind="path" />
            {:else}
                {location}
            {/if}
        </span>
    </span>
</div>

<style>
    .mobile-reading-tab {
        position: relative;
        flex: 0 0 auto;
        width: max-content;
        max-width: 13rem;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: center;
        gap: 0.42rem;
        min-width: 0;
        height: 2.5rem;
        margin-bottom: 0.36rem;
        border: 1px solid var(--reader-tabs-mobile-tab-border);
        border-top-color: var(--reader-tabs-mobile-tab-top-border);
        border-radius: 0 0 0.62rem 0.62rem;
        background: var(--reader-tabs-mobile-tab-bg);
        box-shadow: none;
        color: var(--reader-tabs-mobile-tab-text);
        cursor: pointer;
        font: inherit;
        letter-spacing: 0;
        padding: 0.36rem 0.62rem 0.42rem;
        text-align: left;
        text-transform: none;
        touch-action: pan-x;
        transition:
            background-color 0.16s ease,
            border-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
    }

    .mobile-reading-tab:hover {
        border-color: var(--reader-tabs-mobile-tab-hover-border);
        color: var(--reader-tabs-mobile-tab-hover-text);
        background: var(--reader-tabs-mobile-tab-hover-bg);
    }

    .mobile-reading-tab:active {
        transform: translateY(1px);
    }

    .mobile-reading-tab:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
        z-index: 2;
    }

    .mobile-reading-tab.hasEmoji {
        grid-template-columns: auto minmax(0, 1fr);
    }

    .mobile-reading-tab.selected {
        height: 2.72rem;
        margin-bottom: 0.14rem;
        border-color: var(--reader-tabs-mobile-active-border);
        border-top-color: var(--reader-tabs-mobile-active-bg);
        border-radius: 0 0 0.62rem 0.62rem;
        background: var(--reader-tabs-mobile-active-bg);
        color: var(--reader-tabs-mobile-active-text);
        z-index: 2;
    }

    .mobile-reading-tab.selected::before,
    .mobile-reading-tab.selected::after {
        content: "";
        position: absolute;
        top: -1px;
        width: 0.55rem;
        height: 0.55rem;
        pointer-events: none;
    }

    .mobile-reading-tab.selected::before {
        left: -0.55rem;
        border-top: 1px solid var(--reader-tabs-mobile-active-border);
        border-right: 1px solid var(--reader-tabs-mobile-active-border);
        border-top-right-radius: 0.55rem;
        box-shadow: 0.24rem -0.24rem 0 var(--reader-tabs-mobile-active-bg);
    }

    .mobile-reading-tab.selected::after {
        right: -0.55rem;
        border-top: 1px solid var(--reader-tabs-mobile-active-border);
        border-left: 1px solid var(--reader-tabs-mobile-active-border);
        border-top-left-radius: 0.55rem;
        box-shadow: -0.24rem -0.24rem 0 var(--reader-tabs-mobile-active-bg);
    }

    .tab-icon {
        display: grid;
        place-items: center;
        width: 1.4rem;
        height: 1.4rem;
        border-radius: 0.36rem;
        background: var(--reader-tabs-mobile-tab-icon-bg);
        color: currentColor;
        font-size: 0.82rem;
        line-height: 1;
    }

    .mobile-reading-tab.selected .tab-icon {
        background: var(--reader-tabs-mobile-active-icon-bg);
        color: var(--reader-tabs-mobile-active-icon-text);
    }

    .tab-icon :global(svg) {
        width: 0.82rem;
        height: 0.82rem;
    }

    .tab-emoji {
        display: block;
        max-width: 1rem;
        overflow: hidden;
        text-align: center;
        text-overflow: clip;
        white-space: nowrap;
    }

    .tab-copy {
        display: flex;
        align-items: baseline;
        gap: 0.32rem;
        width: max-content;
        min-width: 0;
        max-width: 100%;
    }

    .tab-title,
    .tab-location {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .tab-title {
        flex: 0 1 auto;
        color: var(--reader-tabs-mobile-tab-title-text, currentColor);
        font-size: 0.74rem;
        font-weight: 780;
        line-height: 1.05;
    }

    .tab-title.untitled {
        font-style: italic;
    }

    .tab-location {
        flex: 0 1 auto;
        max-width: 45%;
        color: var(--reader-tabs-mobile-tab-location-text, var(--reader-tabs-muted));
        font-size: 0.62rem;
        font-weight: 680;
        line-height: 1.05;
    }

    .tab-title :global(.editable-tab-field) {
        max-width: 100%;
    }

    .tab-location :global(.editable-tab-field) {
        max-width: 100%;
    }

    .mobile-reading-tab.selected .tab-title {
        color: var(--reader-tabs-mobile-active-text);
    }

    .mobile-reading-tab.selected .tab-location {
        color: var(--reader-tabs-mobile-active-muted, var(--reader-tabs-active-muted));
    }

    @media only screen and (max-width: 360px) {
        .mobile-reading-tab {
            padding-inline: 0.42rem;
        }

        .tab-icon {
            display: none;
        }
    }
</style>
